import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { OTP_LENGTH } from '../../../../constants';
import { ExceptionCode } from '@api/models';
import type { OtpConfirmDto, OtpRequestDto, UpdateEmailDto } from '@api/models';
import { ModalResult } from '../../../../interfaces/modal-result.interface';
import { CustomerService } from '../../../../services/customer.service';
import { ButtonComponent } from '../../../button/button.component';
import { IconComponent } from '../../../icon/icon.component';
import { OtpComponent } from '../../../otp/otp.component';
import { AutofocusDirective } from '../../../../directives/autofocus.directive';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';
import { resolveHttpErrorCode } from '@/helpers/resolve-http-error-code.helper';
import { MergeAccountsComponent } from '../merge-accounts/merge-accounts.component';

@Component({
  selector: 'tc-update-email-modal',
  templateUrl: './update-email-modal.component.html',
  styleUrls: ['./update-email-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    TranslatePipe,
    OtpComponent,
    IconComponent,
    ReactiveFormsModule,
    ButtonComponent,
    AutofocusDirective,
    MergeAccountsComponent,
  ],
})
export class UpdateEmailModalComponent implements OnInit {
  dialogRef = inject(MatDialogRef<UpdateEmailModalComponent, ModalResult>);
  private destroyRef = inject(DestroyRef);
  private customerService = inject(CustomerService);

  readonly form = new FormGroup({
    email: new FormControl('', [Validators.required]),
    otpDigits: new FormArray(
      new Array(OTP_LENGTH).fill(null).map(() => new FormControl('', Validators.required)),
    ),
  });

  isLoading = signal<boolean>(false);
  error = signal<string>(null);
  state = signal<'input' | 'otp' | 'conflict' | 'success'>('input');
  otpRequest = signal<OtpRequestDto>(null);
  isFormErrorsVisible = signal<boolean>(false);
  /** The email that turned out to belong to another account, and the message explaining that */
  conflictEmail = signal<string>(null);
  conflictMessage = signal<string>(null);
  isMerged = signal<boolean>(false);

  ngOnInit(): void {
    const currentEmail = this.customerService.customer().contactInfo.email;
    this.form.controls.email.setValue(currentEmail);

    this.form.controls.email.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dialogRef.disableClose = true);
  }

  onSubmit(): void {
    this.error.set(null);
    this.isFormErrorsVisible.set(true);

    switch (this.state()) {
      case 'input':
        if (this.form.controls.email.invalid) {
          return;
        }

        const email = this.form.controls.email.getRawValue();
        const currentEmail = this.customerService.customer().contactInfo.email;

        if (email === currentEmail) {
          return;
        }

        this.updateEmailInit();
        break;

      case 'otp':
        this.isLoading.set(true);
        const otpConfirmDto: OtpConfirmDto = {
          requestId: this.otpRequest().requestId,
          otpCode: this.form.controls.otpDigits.getRawValue().join(''),
        };

        this.customerService.updateEmailConfirm(otpConfirmDto)
          .pipe(
            finalize(() => this.isLoading.set(false)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe({
            next: () => {
              this.state.set('success');
              this.dialogRef.disableClose = false;
            },
            error: error => this.handleError(error),
          });
        break;

      case 'success':
        this.dialogRef.close({ type: 'CONFIRM' });
        break;
    }
  }

  /**
   * "Already registered" is not a dead end: the address almost always belongs to another account of the
   * same person, created the first time they logged in by phone. Offer to combine them instead of
   * leaving them stranded mid-checkout.
   */
  private handleError(error: unknown): void {
    const email = this.form.controls.email.getRawValue();
    const message = resolveHttpErrorMessage(error as never);

    if (resolveHttpErrorCode(error as never) === ExceptionCode.CUSTOMER_EMAIL_ALREADY_EXISTS) {
      this.conflictEmail.set(email);
      this.conflictMessage.set(message);
      this.state.set('conflict');
      return;
    }

    // No code - render the raw message exactly as before, so nothing regresses
    this.error.set(message);
  }

  onMerged(): void {
    this.isMerged.set(true);
    this.state.set('success');
    this.dialogRef.disableClose = false;
  }

  onMergeCancelled(): void {
    this.error.set(this.conflictMessage());
    this.state.set('input');
  }

  isControlInvalid(control: FormControl): boolean {
    return this.isFormErrorsVisible() && control.invalid;
  }

  isSubmitDisabled(): boolean {
    if (this.state() !== 'input') {
      return false;
    }

    const email = this.form.controls.email.getRawValue();
    const currentEmail = this.customerService.customer().contactInfo.email;
    return email === currentEmail;
  }

  updateEmailInit(): void {
    this.error.set(null);

    this.isLoading.set(true);
    const dto: UpdateEmailDto = {
      email: this.form.controls.email.getRawValue(),
    };

    this.customerService.updateEmailInit(dto)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: response => {
          this.otpRequest.set(response);
          this.state.set('otp');
        },
        error: error => this.handleError(error),
      });
  }

  goBackFromOtp(): void {
    this.form.controls.otpDigits.reset();
    this.error.set(null);
    this.isFormErrorsVisible.set(false);
    this.state.set('input');
  }
}
